import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DollarSign,
  Package,
  Users,
  TrendingUp,
  TrendingDown,
  MapPin,
  CreditCard,
  Banknote,
  ShoppingCart,
  Truck,
  Clock,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Calendar,
  ArrowRight,
  Store,
  ChevronRight,
  Eye,
  Wallet,
  HandCoins,
} from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtros, setFiltros] = useState({
    fecha_inicio: new Date().toISOString().split("T")[0],
    fecha_fin: new Date().toISOString().split("T")[0],
    zona: "",
  });

  const fetchDashboard = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (filtros.fecha_inicio) params.append("fecha_inicio", filtros.fecha_inicio);
      if (filtros.fecha_fin) params.append("fecha_fin", filtros.fecha_fin);
      if (filtros.zona) params.append("zona", filtros.zona);

      const response = await api.get(`/reportes/dashboard?${params.toString()}`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filtros]);

  useEffect(() => {
    fetchDashboard();
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => fetchDashboard(true), 60000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const handleRefresh = () => fetchDashboard(true);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const fecha = new Date().toLocaleDateString("es-CO", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const hora = new Date().toLocaleTimeString("es-CO", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-[#FBF5E8] to-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#C8902A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#4A2C0A] font-medium">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  // Calculate key metrics
  const totalVentas = dashboard?.total_ventas || 0;
  const totalPedidos = dashboard?.cantidad_transacciones || 0;
  const pedidosPendientes = dashboard?.pedidos_pendientes || 0;
  const pedidosEntregados = dashboard?.pedidos_entregados || 0;
  const recaudoEfectivo = dashboard?.recaudo_efectivo || 0;
  const recaudoTransferencia = dashboard?.recaudo_transferencia || 0;
  const totalRecaudo = recaudoEfectivo + recaudoTransferencia;

  return (
    <div className="space-y-6 pb-8" data-testid="admin-dashboard">
      {/* Header con gradiente */}
      <div className="bg-gradient-to-r from-[#4A2C0A] via-[#6B3D0F] to-[#C8902A] rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <img
                  src="/logo-torta-cuajada.png"
                  alt="Logo"
                  className="w-12 h-12 rounded-full object-cover border-2 border-white/30"
                />
                <div>
                  <h1 className="text-2xl lg:text-3xl font-['Playfair_Display'] font-bold">
                    ¡Buen día, {user?.nombre?.split(' ')[0]}!
                  </h1>
                  <p className="text-white/70 text-sm capitalize">{fecha}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-white/70" />
                <span className="font-mono text-lg">{hora}</span>
              </div>
              <Button
                onClick={handleRefresh}
                disabled={refreshing}
                variant="ghost"
                className="text-white hover:bg-white/10"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Quick Stats in header */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white/70 text-xs uppercase tracking-wider">Ventas Hoy</p>
              <p className="text-2xl lg:text-3xl font-bold mt-1">{formatCurrency(totalVentas)}</p>
              <p className="text-white/60 text-xs mt-1">{totalPedidos} pedidos</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white/70 text-xs uppercase tracking-wider">Recaudo Total</p>
              <p className="text-2xl lg:text-3xl font-bold mt-1">{formatCurrency(totalRecaudo)}</p>
              <div className="flex gap-2 mt-1">
                <span className="text-green-300 text-xs flex items-center gap-1">
                  <Banknote className="w-3 h-3" />
                  {formatCurrency(recaudoEfectivo)}
                </span>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white/70 text-xs uppercase tracking-wider">Entregados</p>
              <p className="text-2xl lg:text-3xl font-bold mt-1">{pedidosEntregados}</p>
              <p className="text-green-300 text-xs mt-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Completados hoy
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white/70 text-xs uppercase tracking-wider">Pendientes</p>
              <p className="text-2xl lg:text-3xl font-bold mt-1">{pedidosPendientes}</p>
              <p className="text-amber-300 text-xs mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Por gestionar
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros compactos */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#E5D4B3]">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-[#4A2C0A]">
            <Calendar className="w-4 h-4" />
            <span className="text-sm font-medium">Filtrar:</span>
          </div>
          <Input
            type="date"
            value={filtros.fecha_inicio}
            onChange={(e) => setFiltros({ ...filtros, fecha_inicio: e.target.value })}
            className="w-auto h-9 text-sm border-[#E5D4B3]"
          />
          <span className="text-[#4A2C0A]/50">a</span>
          <Input
            type="date"
            value={filtros.fecha_fin}
            onChange={(e) => setFiltros({ ...filtros, fecha_fin: e.target.value })}
            className="w-auto h-9 text-sm border-[#E5D4B3]"
          />
          <Select
            value={filtros.zona || "todas"}
            onValueChange={(value) => setFiltros({ ...filtros, zona: value === "todas" ? "" : value })}
          >
            <SelectTrigger className="w-[150px] h-9 text-sm border-[#E5D4B3]">
              <MapPin className="w-3 h-3 mr-1" />
              <SelectValue placeholder="Zona" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las zonas</SelectItem>
              {dashboard?.zonas_disponibles?.map((z) => (
                <SelectItem key={z} value={z}>{z}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={() => fetchDashboard(true)} 
            size="sm"
            className="bg-[#C8902A] hover:bg-[#A67720]"
          >
            Aplicar
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          onClick={() => navigate('/admin/catalogo')}
          className="bg-white rounded-xl p-4 shadow-sm border border-[#E5D4B3] hover:border-[#C8902A] hover:shadow-md transition-all group text-left"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
            </div>
            <ChevronRight className="w-5 h-5 text-[#4A2C0A]/30 group-hover:text-[#C8902A] transition-colors" />
          </div>
          <p className="font-semibold text-[#4A2C0A]">Pedidos Online</p>
          <p className="text-xs text-[#4A2C0A]/60 mt-1">
            {dashboard?.pedidos_nuevos || 0} nuevos por gestionar
          </p>
        </button>

        <button
          onClick={() => navigate('/admin/despachos')}
          className="bg-white rounded-xl p-4 shadow-sm border border-[#E5D4B3] hover:border-[#C8902A] hover:shadow-md transition-all group text-left"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
              <Truck className="w-5 h-5 text-green-600" />
            </div>
            <ChevronRight className="w-5 h-5 text-[#4A2C0A]/30 group-hover:text-[#C8902A] transition-colors" />
          </div>
          <p className="font-semibold text-[#4A2C0A]">Despachos</p>
          <p className="text-xs text-[#4A2C0A]/60 mt-1">
            {dashboard?.rutas_activas || 0} rutas activas hoy
          </p>
        </button>

        <button
          onClick={() => navigate('/admin/vendedores')}
          className="bg-white rounded-xl p-4 shadow-sm border border-[#E5D4B3] hover:border-[#C8902A] hover:shadow-md transition-all group text-left"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <ChevronRight className="w-5 h-5 text-[#4A2C0A]/30 group-hover:text-[#C8902A] transition-colors" />
          </div>
          <p className="font-semibold text-[#4A2C0A]">Vendedores</p>
          <p className="text-xs text-[#4A2C0A]/60 mt-1">
            {dashboard?.vendedores_activos || 0} activos hoy
          </p>
        </button>

        <button
          onClick={() => navigate('/admin/reportes')}
          className="bg-white rounded-xl p-4 shadow-sm border border-[#E5D4B3] hover:border-[#C8902A] hover:shadow-md transition-all group text-left"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-amber-600" />
            </div>
            <ChevronRight className="w-5 h-5 text-[#4A2C0A]/30 group-hover:text-[#C8902A] transition-colors" />
          </div>
          <p className="font-semibold text-[#4A2C0A]">Reportes</p>
          <p className="text-xs text-[#4A2C0A]/60 mt-1">Ver análisis detallado</p>
        </button>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Columna izquierda - 2 columnas */}
        <div className="lg:col-span-2 space-y-6">
          {/* Recaudo del día */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5D4B3]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Playfair_Display'] text-lg text-[#4A2C0A] font-semibold flex items-center gap-2">
                <HandCoins className="w-5 h-5 text-[#C8902A]" />
                Recaudo del Día
              </h2>
              <span className="text-xs text-[#4A2C0A]/60 bg-[#FBF5E8] px-2 py-1 rounded-full">
                En tiempo real
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-4 border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center">
                    <Banknote className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-green-700 text-xs font-medium uppercase tracking-wider">Efectivo</p>
                    <p className="text-2xl font-bold text-green-800">{formatCurrency(recaudoEfectivo)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-blue-700 text-xs font-medium uppercase tracking-wider">Transferencia</p>
                    <p className="text-2xl font-bold text-blue-800">{formatCurrency(recaudoTransferencia)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Barra de progreso de recaudo */}
            <div className="bg-[#FBF5E8] rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#4A2C0A]/70">Recaudo vs Ventas</span>
                <span className="text-sm font-semibold text-[#4A2C0A]">
                  {totalVentas > 0 ? Math.round((totalRecaudo / totalVentas) * 100) : 0}%
                </span>
              </div>
              <div className="h-3 bg-[#E5D4B3] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#C8902A] to-[#4A2C0A] rounded-full transition-all duration-500"
                  style={{ width: `${totalVentas > 0 ? Math.min((totalRecaudo / totalVentas) * 100, 100) : 0}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-[#4A2C0A]/60">
                <span>Recaudado: {formatCurrency(totalRecaudo)}</span>
                <span>Total Ventas: {formatCurrency(totalVentas)}</span>
              </div>
            </div>
          </div>

          {/* Top Vendedores */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5D4B3]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Playfair_Display'] text-lg text-[#4A2C0A] font-semibold flex items-center gap-2">
                <Users className="w-5 h-5 text-[#C8902A]" />
                Top Vendedores Hoy
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/vendedores')}
                className="text-[#C8902A] hover:text-[#A67720]"
              >
                Ver todos
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {dashboard?.top_vendedores?.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 mx-auto mb-3 text-[#E5D4B3]" />
                <p className="text-[#4A2C0A]/60">Sin ventas registradas hoy</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dashboard?.top_vendedores?.slice(0, 5).map((v, index) => (
                  <div 
                    key={v.id} 
                    className={`flex items-center gap-4 p-3 rounded-xl transition-colors ${
                      index === 0 ? 'bg-gradient-to-r from-amber-50 to-amber-100/50 border border-amber-200' : 'bg-[#FBF5E8]/50 hover:bg-[#FBF5E8]'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${
                      index === 0 ? "bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg" :
                      index === 1 ? "bg-gradient-to-br from-gray-300 to-gray-400" :
                      index === 2 ? "bg-gradient-to-br from-amber-600 to-amber-700" :
                      "bg-[#C8902A]/50"
                    }`}>
                      {index + 1}
                    </div>
                    
                    {v.foto_url ? (
                      <img src={v.foto_url} alt={v.nombre} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 border-white shadow-sm" />
                    ) : (
                      <div className="w-10 h-10 bg-[#C8902A]/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-[#C8902A] font-semibold">{v.nombre?.charAt(0)}</span>
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#4A2C0A] truncate">{v.nombre}</p>
                      <div className="flex items-center gap-2 text-xs text-[#4A2C0A]/60">
                        <MapPin className="w-3 h-3" />
                        <span>{v.zona || 'Sin zona'}</span>
                        <span className="text-[#E5D4B3]">•</span>
                        <span>{v.unidades} und</span>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="font-bold text-lg text-[#C8902A]">{formatCurrency(v.total)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Columna derecha */}
        <div className="space-y-6">
          {/* Estado de Pedidos */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5D4B3]">
            <h2 className="font-['Playfair_Display'] text-lg text-[#4A2C0A] font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-[#C8902A]" />
              Estado de Pedidos
            </h2>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <span className="text-amber-800 font-medium">Nuevos</span>
                </div>
                <span className="text-xl font-bold text-amber-700">{dashboard?.pedidos_nuevos || 0}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-3">
                  <Store className="w-5 h-5 text-blue-600" />
                  <span className="text-blue-800 font-medium">En Producción</span>
                </div>
                <span className="text-xl font-bold text-blue-700">{dashboard?.pedidos_produccion || 0}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl border border-purple-200">
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-purple-600" />
                  <span className="text-purple-800 font-medium">En Ruta</span>
                </div>
                <span className="text-xl font-bold text-purple-700">{dashboard?.pedidos_ruta || 0}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-200">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-medium">Entregados</span>
                </div>
                <span className="text-xl font-bold text-green-700">{pedidosEntregados}</span>
              </div>
            </div>
          </div>

          {/* Productos más vendidos */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5D4B3]">
            <h2 className="font-['Playfair_Display'] text-lg text-[#4A2C0A] font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#C8902A]" />
              Más Vendidos Hoy
            </h2>

            {!dashboard?.productos_vendidos || Object.keys(dashboard.productos_vendidos).length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 mx-auto mb-3 text-[#E5D4B3]" />
                <p className="text-[#4A2C0A]/60 text-sm">Sin productos vendidos</p>
              </div>
            ) : (
              <div className="space-y-2">
                {Object.entries(dashboard.productos_vendidos)
                  .sort((a, b) => b[1].cantidad - a[1].cantidad)
                  .slice(0, 5)
                  .map(([nombre, data], index) => (
                    <div key={nombre} className="flex items-center gap-3 p-2 bg-[#FBF5E8]/50 rounded-lg">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 ? 'bg-[#C8902A] text-white' : 'bg-[#E5D4B3] text-[#4A2C0A]'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#4A2C0A] truncate">{nombre}</p>
                        <p className="text-xs text-[#4A2C0A]/60">{data.cantidad} unidades</p>
                      </div>
                      <span className="text-sm font-semibold text-[#C8902A]">
                        {formatCurrency(data.total)}
                      </span>
                    </div>
                  ))
                }
              </div>
            )}
          </div>

          {/* Alertas */}
          {(dashboard?.alertas?.length > 0 || dashboard?.transferencias_pendientes > 0) && (
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border border-red-200">
              <h2 className="font-['Playfair_Display'] text-lg text-red-800 font-semibold mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Alertas
              </h2>
              
              <div className="space-y-2">
                {dashboard?.transferencias_pendientes > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-white/80 rounded-lg">
                    <CreditCard className="w-5 h-5 text-orange-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-orange-800">
                        {dashboard.transferencias_pendientes} transferencias pendientes
                      </p>
                      <p className="text-xs text-orange-600">Requieren confirmación</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate('/admin/catalogo')}
                      className="text-orange-700"
                    >
                      Ver
                    </Button>
                  </div>
                )}
                
                {dashboard?.alertas?.map((alerta, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-white/80 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <p className="text-sm text-red-800">{alerta}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
